/* eslint-env browser */
// Add / edit a car (wave 3, W2_Portal_Settings look): one card, one label
// above each field, what is wrong under the field, one primary button.
//
// Reads and writes are the old page's, unchanged:
// - the cars list is fetched once per mount only when the slice is empty
//   (a fallback); an existing car starts from ONE fresh read of /cars/<id>;
// - Update: editCar(car with fleetadmin from the driver, no tableData,
//   'Update'); when the driver changed on an active car, the old driver's car
//   fields are cleared (updateUserCar) before going back;
// - Add: a fresh indexed read of the driver's cars to find the active one,
//   then (as before) either deactivate it and mirror this car onto the driver
//   (updateUserCar with carType, vehicleNumber, ... — the vehicle-placeholder
//   logic depends on this plate mirror), or keep this car inactive, or make it
//   the active car and mirror it; approved from settings.carApproval; then
//   editCar(car, 'Add').
// Saving needs critical edits on (settings.AllowCriticalEditsAdmin).
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { get } from 'firebase/database';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { api, FirebaseContext } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import CircularLoading from '../components/CircularLoading';
import { loadCars, markCarsStale } from '../utils/carsSession';
import { showToast, InlineMessage } from '../components/feedback';
import { BackButton, PageHeader, SectionCard, FieldLabel, FieldError, calmCaps } from '../components/modern';

const EditCar = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { editCar, updateUserCar, fetchCars } = api;
  const { carEditRef, carsByDriverRef } = useContext(FirebaseContext);
  const [fleetMapObj, setFleetMapObj] = useState([]);
  const [role, setRole] = useState(null);
  const userdata = useSelector((state) => state.usersdata);
  const [drivers, setDrivers] = useState([]);
  const cartypes = useSelector((state) => state.cartypes);
  const carlistdata = useSelector((state) => state.carlistdata);
  const [driverData, setDriverData] = useState(null);
  const [data, setData] = useState(null);
  const [driverInputValue, setDriverInputValue] = useState('');
  const [oldData, setOldData] = useState(null);
  const [carTypeAvailable, setCarTypeAvailable] = useState(null);
  const [carData, setCardata] = useState();
  const [freshCar, setFreshCar] = useState(null);
  // Id whose fresh read has settled (landed or failed). Add has nothing to wait for.
  const [freshSettledFor, setFreshSettledFor] = useState(null);
  const freshSettled = !id || freshSettledFor === id.toString();
  const populatedFor = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState(null);
  const { state } = useLocation();

  // Cars are fetched ONCE per mount, and only if the slice is empty at mount
  // time. The slice object itself must NOT be an effect dependency: FETCH_CARS
  // replaces the slice (loading: true) without setting `cars`, so a guard that
  // re-runs on slice changes re-dispatches every render until the 5.8 MB
  // response lands (this looped ~1,360 full /cars reads in 2 minutes in prod).
  // This page only needs the list as a FALLBACK (the form starts from the
  // single-car read below), so any non-empty slice will do — a stale session
  // must not cost another 5.8 MB here. When it does fetch, it goes through
  // loadCars so the download is recorded for the list views (no second full
  // read on the next Cars list mount) and shares a load already in flight.
  const carsLoadedAtMount = !!(carlistdata && carlistdata.cars);
  useEffect(() => {
    if (!carsLoadedAtMount) loadCars(dispatch, fetchCars, auth.profile.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The cars list is loaded once per session, so the slice copy of this car
  // can be hours old — and Update writes the WHOLE record (set). Start the form
  // from the car's current server copy (one ~1 KB read) so a save cannot put
  // back an active flag / approval / image changed since by the driver's app
  // or another staff member. Falls back to the slice copy if the read fails.
  // The form stays behind the loading component until this read has settled,
  // so it is never reset under the user when the fresh copy lands.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    get(carEditRef(id))
      .then((snap) => {
        if (cancelled) return;
        if (snap.val()) {
          setFreshCar({ ...snap.val(), id: id.toString() });
          setFreshSettledFor(id.toString());
        } else {
          // Deleted since the list was loaded: saving would re-create it.
          markCarsStale();
          navigate('/404');
        }
      })
      .catch(() => {
        if (!cancelled) setFreshSettledFor(id.toString());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Populate ONCE per id (populatedFor): a later slice refetch or a profile
  // listener tick must never replace what the user has typed. The deps are
  // values only — never the carlistdata / auth objects.
  const selfDriverUid = auth.profile && auth.profile.usertype === 'driver' ? auth.profile.uid : '';
  useEffect(() => {
    const key = id ? id.toString() : 'new';
    if (populatedFor.current === key) return;
    if (id) {
      if (!freshSettled) return;
      const useFresh = !!(freshCar && freshCar.id === key);
      // Fresh read failed: wait for the slice copy.
      if (!useFresh && !carlistdata.cars) return;
      const found = useFresh ? freshCar : carlistdata.cars.filter((item) => item.id === key)[0];
      if (!found) {
        navigate('/404');
      }
      setData(found);
      setOldData(found);
      populatedFor.current = key;
    } else {
      setData({
        createdAt: new Date().getTime(),
        car_image: 'https://cdn.pixabay.com/photo/2012/04/13/20/37/car-33556__480.png',
        vehicleNumber: '',
        vehicleMake: '',
        carType: '',
        driver: selfDriverUid,
        vehicleModel: '',
        other_info: '',
        active: false,
      });
      populatedFor.current = key;
    }
  }, [carlistdata.cars, id, navigate, freshCar, freshSettled, selfDriverUid]);

  useEffect(() => {
    if (carlistdata.cars) {
      setCardata(carlistdata.cars);
    } else {
      setCardata([]);
    }
  }, [carlistdata.cars]);

  useEffect(() => {
    const checkCar = ((cartypes && cartypes.cars) || []).filter((item) => item.name === oldData?.carType)[0];
    if (checkCar) {
      setCarTypeAvailable(true);
    } else {
      setCarTypeAvailable(false);
    }
  }, [cartypes, oldData]);

  useEffect(() => {
    if (role !== 'driver' && userdata.users) {
      let arr = userdata.users.filter(
        (user) =>
          user.usertype === 'driver' &&
          ((role === 'fleetadmin' && user.fleetadmin && user.fleetadmin === auth.profile.uid) ||
            role === 'admin' ||
            role === 'callcentersupervisor' ||
            role === 'callcenteragent')
      );
      let obj2 = {};
      let arr2 = [];
      for (let i = 0; i < arr.length; i++) {
        let user = arr[i];
        arr2.push({
          id: user.id,
          desc:
            user.firstName +
            ' ' +
            user.lastName +
            ' (' +
            (settings.AllowCriticalEditsAdmin ? user.mobile : t('hidden_demo')) +
            ') ' +
            (settings.AllowCriticalEditsAdmin ? user.email : t('hidden_demo')),
        });
        obj2[user.id] = user.fleetadmin ? user.fleetadmin : null;
      }
      setDrivers(arr2);
      setFleetMapObj(obj2);
    }
  }, [userdata.users, settings.AllowCriticalEditsAdmin, role, auth.profile.uid, t]);

  useEffect(() => {
    setDriverData(
      auth.profile.firstName +
        ' ' +
        auth.profile.lastName +
        ' (' +
        (settings.AllowCriticalEditsAdmin ? auth.profile.mobile : t('hidden_demo')) +
        ') ' +
        (settings.AllowCriticalEditsAdmin ? auth.profile.email : t('hidden_demo'))
    );
  }, [auth.profile.lastName, auth.profile.mobile, auth.profile.email, settings.AllowCriticalEditsAdmin, auth.profile.firstName, t]);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleDriverChange = (event, newValue) => {
    setData({ ...data, driver: newValue?.id || '' });
  };

  const handleDriverInputChange = (event, newInputValue) => {
    setDriverInputValue(newInputValue);
  };

  const getDriverValue = () => {
    if (!data?.driver) return null;
    const driver = drivers.find((d) => d.id === data.driver);
    return driver || null;
  };
  const handelChangeCarType = (event) => {
    setData({ ...data, carType: event.target.value });
  };
  const backToList = () => navigate('/cars', { state: { pageNo: state?.pageNo } });

  // What a new car needs, in the old order, with the old messages.
  const addErrors = {};
  if (data && !id) {
    if (!data.vehicleNumber) addErrors.vehicleNumber = t('car_no_not_found');
    if (!data.vehicleMake) addErrors.vehicleMake = t('vehicleMake_required');
    if (!data.vehicleModel) addErrors.vehicleModel = t('vehicleModel_required');
    if (!data.driver) addErrors.driver = t('driver_required');
    if (!data.carType) addErrors.carType = t('carType_required');
  }
  const errorOf = (key) => (showErrors ? addErrors[key] : null);

  const updateCar = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (data === oldData) {
      setFormError(t('make_changes_to_update'));
      return;
    }
    setLoading(true);
    const car = { ...data };
    car['fleetadmin'] = car['driver'] ? (fleetMapObj[car['driver']] ? fleetMapObj[car['driver']] : null) : null;
    delete car.tableData;
    dispatch(editCar(car, 'Update'));
    // EDIT_CAR is a no-op reducer: make the cars list refetch once.
    markCarsStale();
    if (car.driver !== oldData.driver && oldData.driver && oldData.active) {
      // Await so the old driver's cleared car fields are cache-coherent
      // before we navigate (their compliance updates immediately).
      await dispatch(
        updateUserCar(oldData.driver, {
          carType: null,
          vehicleNumber: null,
          vehicleMake: null,
          vehicleModel: null,
          other_info: null,
          car_image: null,
          updateAt: new Date().getTime(),
        })
      );
    }
    setLoading(false);
    showToast.success(t('saved', { defaultValue: 'Saved' }));
    backToList();
  };

  const addCar = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    setShowErrors(true);
    const firstBad = ['vehicleNumber', 'vehicleMake', 'vehicleModel', 'driver', 'carType'].find((key) => addErrors[key]);
    if (firstBad) {
      const el = document.getElementById(`ec-${firstBad}`);
      if (el && typeof el.focus === 'function') el.focus();
      return;
    }
    setLoading(true);
    const car = { ...data };
    // Await the driver-record write (updateUserCar) before navigating —
    // otherwise the next page's cache-first user fetch reads a not-yet-patched
    // cache and the driver's compliance status reverts to "no car".
    let activeCar = null;
    let updateData = {
      carType: car.carType,
      vehicleNumber: car.vehicleNumber,
      vehicleMake: car.vehicleMake,
      vehicleModel: car.vehicleModel,
      other_info: car.other_info,
      car_image: car.car_image,
      updateAt: new Date().getTime(),
    };
    // Look for the driver's active car in a FRESH indexed read of just this
    // driver's cars (tiny): the session's list can be hours old, and a car the
    // driver activated from the app since then would otherwise end up as a
    // second active car.
    let driverCars = carData;
    try {
      const snap = await get(carsByDriverRef(car.driver));
      const val = snap.val() || {};
      driverCars = Object.keys(val).map((k) => ({ ...val[k], id: k }));
    } catch (_) {
      /* fall back to the session's list */
    }
    for (let i = 0; i < driverCars.length; i++) {
      if (driverCars[i].driver === car.driver && driverCars[i].active) {
        activeCar = driverCars[i];
        break;
      }
    }
    if (activeCar && car.active) {
      activeCar.active = false;
      dispatch(editCar(activeCar, 'Update'));
      await dispatch(updateUserCar(car.driver, updateData));
    } else if (activeCar && !car.active) {
      car.active = false;
    } else {
      car.active = true;
      await dispatch(updateUserCar(car.driver, updateData));
    }
    car['createdAt'] = new Date().getTime();
    car['fleetadmin'] = car['driver'] ? (fleetMapObj[car['driver']] ? fleetMapObj[car['driver']] : null) : null;
    if (!settings.carApproval) {
      car['approved'] = true;
      if (car.active) {
        await dispatch(updateUserCar(car.driver, { carApproved: true }));
      }
    } else {
      car['approved'] = false;
    }
    dispatch(editCar(car, 'Add'));
    // EDIT_CAR is a no-op reducer: make the cars list refetch once.
    markCarsStale();
    showToast.success(t('saved', { defaultValue: 'Saved' }));
    navigate('/cars');
    setLoading(false);
  };

  const handleSave = async () => {
    if (loading) return;
    setFormError(null);
    try {
      if (id) await updateCar();
      else await addCar();
    } catch (e) {
      setLoading(false);
      setFormError(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
    }
  };

  if (id && !freshSettled) return <CircularLoading />;
  if (!data) return <CircularLoading />;

  const locked = !!data.active || loading;
  const textField = (key, label) => (
    <Box key={key} sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={`ec-${key}`} disabled={locked}>
        {label}
      </FieldLabel>
      <TextField
        id={`ec-${key}`}
        inputProps={{ autoComplete: 'off', 'aria-describedby': errorOf(key) ? `ec-${key}-error` : undefined }}
        fullWidth
        value={data[key] || ''}
        disabled={locked}
        error={!!errorOf(key)}
        onChange={(e) => setData({ ...data, [key]: e.target.value })}
      />
      <FieldError id={`ec-${key}-error`}>{errorOf(key)}</FieldError>
    </Box>
  );
  const carTypeName = (name) => (name ? t(getLangKey(name), { defaultValue: name }) : '');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={backToList} />
      <PageHeader title={calmCaps(t(id ? 'update_car_title' : 'add_car_title'))} />
      {formError ? (
        <InlineMessage kind="error" onClose={() => setFormError(null)}>
          {formError}
        </InlineMessage>
      ) : null}
      <SectionCard id="ec-car" icon={<DirectionsCarOutlinedIcon />} hue="accent" title={t('vehicle', { defaultValue: 'Vehicle' })}>
        {/* One column on a phone, two from md (as before), three from lg. */}
        <Box
          sx={{
            display: 'grid',
            columnGap: 2.5,
            rowGap: 2.25,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            mt: 1.5,
          }}
        >
          {textField('vehicleNumber', t('vehicle_reg_no'))}
          {textField('vehicleMake', t('vehicle_model_name'))}
          {textField('vehicleModel', t('vehicle_model_no'))}
          {textField('other_info', t('other_info'))}

          <Box sx={{ minWidth: 0 }}>
            <FieldLabel htmlFor="ec-driver" disabled={role === 'driver' || loading}>
              {t('driver')}
            </FieldLabel>
            <Autocomplete
              id="ec-driver"
              options={role === 'driver' ? [] : drivers || []}
              getOptionLabel={(option) => option.desc || ''}
              value={role === 'driver' ? { id: auth.profile.id, desc: driverData } : getDriverValue()}
              onChange={handleDriverChange}
              onInputChange={handleDriverInputChange}
              inputValue={driverInputValue}
              disabled={role === 'driver' || loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errorOf('driver')}
                  inputProps={{ ...params.inputProps, 'aria-describedby': errorOf('driver') ? 'ec-driver-error' : undefined }}
                />
              )}
              noOptionsText={t('no_options', { defaultValue: 'No options' })}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <FieldError id="ec-driver-error">{errorOf('driver')}</FieldError>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <FieldLabel htmlFor="ec-carType" disabled={locked}>
              {t('car_type')}
            </FieldLabel>
            <TextField
              id="ec-carType"
              select
              fullWidth
              value={data.carType || ''}
              onChange={handelChangeCarType}
              disabled={locked}
              error={!!errorOf('carType')}
              SelectProps={{ SelectDisplayProps: { 'aria-describedby': errorOf('carType') ? 'ec-carType-error' : undefined } }}
            >
              {!carTypeAvailable && oldData?.carType ? <MenuItem value={oldData.carType}>{carTypeName(oldData.carType)}</MenuItem> : null}
              {cartypes?.cars
                ? cartypes.cars.map((e) => (
                    <MenuItem key={e.id} value={e.name}>
                      {carTypeName(e.name)}
                    </MenuItem>
                  ))
                : null}
            </TextField>
            <FieldError id="ec-carType-error">{errorOf('carType')}</FieldError>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          >
            {loading ? t('form_saving', { defaultValue: 'Saving…' }) : calmCaps(id ? t('update', { defaultValue: 'Update' }) : t('add'))}
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default EditCar;
